import { mgmtAxios, MgmtEnvelope, getMgmtMemberToken } from "./mgmt";

/**
 * 全家歸還 → 日翊退貨通建單(取 QRCode 寄件編號)。
 *
 * 對應新版後端:POST /api/guest/rentals/returnship/fme(免登入,對齊訪客歸還)。
 * QRCode 與畫面顯示都用 details.order_no(11 碼,日翊機台規格;
 * 全家窗口確認檢核碼不需要)。details.shipping_no(12 碼)後端仍回傳,前端已不使用。
 */
export interface FmeReturnshipData {
  shipping_no: string;
  order_no: string;
  parent_id: string;
  product_name: string;
  status: "success" | "simulated" | string;
  /** 交件期限,超過日翊會作廢這張編號 */
  expires_at?: string | null;
  /** 重掃回同一張時,畫面要能說「已完成交件」 */
  flow_label?: "pending" | "consigned" | "expired" | "failed";
}

/** 會員名下還沒去全家交件的寄件單(PG-209 會員中心重看用) */
export interface PendingFamiShipment {
  id: number;
  rental_log_id: number | null;
  packaging_id: string;
  order_no: string;
  created_at: string | null;
  expires_at: string | null;
  flow_label: "pending" | "consigned" | "expired" | "failed";
}

export const listPendingFamiShipments = async (): Promise<PendingFamiShipment[]> => {
  const token = getMgmtMemberToken();
  if (!token) return [];
  const { data } = await mgmtAxios.request<MgmtEnvelope<PendingFamiShipment[]>>({
    url: `/api/member/rentals/returnship/fme/pending`,
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  return data.details || [];
};

export interface CreateFmeReturnshipInput {
  epk_id: string;
  sender_name?: string;
  sender_phone?: string;
  rental_log_id?: number;
}

export const createFmeReturnship = async (
  input: CreateFmeReturnshipInput
): Promise<FmeReturnshipData> => {
  const { data } = await mgmtAxios.request<MgmtEnvelope<FmeReturnshipData>>({
    url: `/api/guest/rentals/returnship/fme`,
    method: "POST",
    data: {
      packaging_id: input.epk_id,
      sender_name: input.sender_name,
      sender_phone: input.sender_phone,
      rental_log_id: input.rental_log_id,
    },
  });

  return data.details;
};

export interface SimulateFmeReturnshipInput {
  epk_id: string;
  step?: "consigned" | "dc_received" | "abnormal" | "all";
}

export interface SimulateFmeReturnshipData {
  shipping_no: string | null;
  flow_status: string | null;
  applied: string[];
}

/**
 * 測試用:模擬日翊回拋(店員掃描 → 交寄 B02),推進退貨通節點,
 * 讓後台包材頁看得到「已交寄門市」。僅測試站呼叫;正式環境後端一律回 403。
 * step 預設 consigned(店員掃描 = 交寄)。
 */
export const simulateFmeReturnship = async (
  input: SimulateFmeReturnshipInput
): Promise<SimulateFmeReturnshipData> => {
  const { data } = await mgmtAxios.request<
    MgmtEnvelope<SimulateFmeReturnshipData>
  >({
    url: `/api/guest/rentals/returnship/fme/simulate`,
    method: "POST",
    data: { packaging_id: input.epk_id, step: input.step },
  });

  return data.details;
};

/**
 * 顧客按「領取優惠券」、在 LIFF 綁定完成後呼叫:後端推 Step 1 引導訊息到他的 LINE。
 * 免登入,身分靠 claim_token。失敗不擋流程(訊息推不出去頂多少一則,導轉照走)。
 */
export const sendFmeClaimGuide = async (
  idOrToken: { claimToken?: string; rentalLogId?: number }
): Promise<void> => {
  try {
    await mgmtAxios.request({
      url: `/api/guest/rentals/returnship/fme/claim-guide`,
      method: "POST",
      // 訪客帶 claim_token,已登入會員沒有 token,帶歸還單號
      data: idOrToken.claimToken
        ? { claim_token: idOrToken.claimToken }
        : { rental_log_id: idOrToken.rentalLogId },
    });
  } catch (e) {
    console.log("sendFmeClaimGuide failed", e);
  }
};
